// utils/comisionEdificiosUtil.js

/**
 * Resuelve la mejor fecha disponible para determinar el período de comisión.
 * Prioridad: facturafecha > mes_cierre (MM-YYYY) > mes+ano > hoy
 */
export const resolverFechaFactura = (factura) => {
  if (factura.facturafecha) return factura.facturafecha;
  if (factura.mes_cierre) {
    const [mes, ano] = factura.mes_cierre.split("-");
    return `${ano}-${mes}-01`;
  }
  if (factura.ano && factura.mes) {
    return `${factura.ano}-${String(factura.mes).padStart(2, "0")}-01`;
  }
  return new Date().toISOString();
};

/**
 * Calcula el porcentaje de comisión del edificio según la fecha de factura
 * Prioridad: rango 1 > rango 2 > rango 3
 * Fallback a comision_3 si no matchea ningún rango
 */
export const calcularPorcentajeComisionEdificio = (edificio, fechaFacturaStr) => {
  if (!edificio || !fechaFacturaStr) return 0;

  const fechaFactura = new Date(fechaFacturaStr);
  if (isNaN(fechaFactura.getTime())) return edificio.comision_3_porcentaje || 0;

  const rangos = [
    {
      inicio: edificio.comision_1_fechainicio,
      fin: edificio.comision_1_fechafin,
      porcentaje: edificio.comision_1_porcentaje || 0,
    },
    {
      inicio: edificio.comision_2_fechainicio,
      fin: edificio.comision_2_fechafin,
      porcentaje: edificio.comision_2_porcentaje || 0,
    },
    {
      inicio: edificio.comision_3_fechainicio,
      fin: edificio.comision_3_fechafin,
      porcentaje: edificio.comision_3_porcentaje || 0,
    },
  ];

  for (const rango of rangos) {
    if (rango.inicio && rango.fin) {
      const inicio = new Date(rango.inicio);
      const fin = new Date(rango.fin);
      if (fechaFactura >= inicio && fechaFactura <= fin) {
        return rango.porcentaje;
      }
    }
  }

  return edificio.comision_3_porcentaje || 0;
};

/**
 * Calcula pantallas ajustadas del edificio en una factura (pauta)
 * Copia exacta de la lógica en CalculadorComisionEdificio
 */
export const calcularPantallasAjustadas = (edificio, factura) => {
  const { pantallas_ascensores, pantallas_espera, pantallas_circulacion, pantallas_cowork } = factura || {};
  const edificioId = String(edificio.id);

  let total = 0;
  if (pantallas_ascensores?.split(",").includes(edificioId)) total += edificio.pantallas || 0;
  if (pantallas_espera?.split(",").includes(edificioId)) total += edificio.espera || 0;
  if (pantallas_circulacion?.split(",").includes(edificioId)) total += edificio.totems || 0;
  if (pantallas_cowork?.split(",").includes(edificioId)) total += edificio.cowork || 0;

  return total;
};

/**
 * Comisión edificios para factura PAUTA (multi-edificio)
 */
export const calcularComisionEdificioPauta = (factura, edificio, netoFacturaTotal) => {
  const pantallasAjustadas = calcularPantallasAjustadas(edificio, factura);
  const proporcion = factura.pantallastotales > 0 ? pantallasAjustadas / factura.pantallastotales : 0;
  const porcentaje = calcularPorcentajeComisionEdificio(edificio, resolverFechaFactura(factura));
  return netoFacturaTotal * proporcion * (porcentaje / 100);
};

/**
 * Comisión edificios para BTL (ya prorrateado por building)
 * En BTL el neto ya viene descontada la comisión agencias en el building
 */
export const calcularComisionEdificioBTL = (oportunidadBTL, edificioId) => {
  const buildings = oportunidadBTL.buildings || [];
  const building = buildings.find((b) => b.id === edificioId);
  if (!building) return 0;

  const edificio = todosEdificios.find((e) => e.id === edificioId); // Necesitarás pasar edificiosMap o buscar
  if (!edificio) return 0;

  const porcentaje = calcularPorcentajeComisionEdificio(edificio, resolverFechaFactura(oportunidadBTL));
  const netoBuilding = building.importe - (building.importe * (building.comision || 0)) / 100;
  return netoBuilding * (porcentaje / 100);
};

/**
 * Función maestra: comisión edificios total para una oportunidad
 */
export const calcularComisionEdificiosTotal = (oportunidad, edificiosMap, netoDespuesAgencias) => {
  if (oportunidad.isBTL) {
    // BTL: suma por cada building
    const buildings = oportunidad.buildings || [];
    return Math.round(
      buildings.reduce((total, building) => {
        const edificio = edificiosMap[building.id];
        if (!edificio) return total;
        const porcentaje = calcularPorcentajeComisionEdificio(edificio, resolverFechaFactura(oportunidad));
        const netoBuilding = building.importe;
        return total + netoBuilding * (porcentaje / 100);
      }, 0)
    );
  } else {
    // PAUTA: prorrateo por pantallas
    const edificiosParticipantes = oportunidad.edificiosparticipantes
      ? [...new Set(oportunidad.edificiosparticipantes.split(",").map(Number))]
      : [];
    return Math.round(
      edificiosParticipantes.reduce((total, edificioId) => {
        const edificio = edificiosMap[edificioId];
        if (!edificio) return total;
        return total + calcularComisionEdificioPauta(oportunidad, edificio, netoDespuesAgencias);
      }, 0)
    );
  }
};
